// 存档系统 - 支持多存档槽、自动存档、导入导出
import { GameSaveData } from './GameState.js';

// 存档元数据
export interface SaveSlotMeta {
  id: number;
  name: string;
  preview: string;
  updatedAt: number;
  playTime: number;
  playerName: string;
  playerAge: number;
  currentChapter: string;
}

// 存档管理器
export class SaveManager {
  private static readonly SAVE_KEY_PREFIX = 'huanhuan_game_save_';
  private static readonly META_KEY = 'huanhuan_game_meta';
  private static readonly MAX_SLOTS = 10;
  private static readonly AUTOSAVE_INTERVAL = 5 * 60 * 1000; // 5分钟
  
  private autosaveTimer: number | null = null;
  private currentSlot: number = 0;
  
  // 获取存档元数据列表
  getSaveSlots(): SaveSlotMeta[] {
    const meta = this.loadMeta();
    return meta.slots || [];
  }
  
  // 加载元数据
  private loadMeta(): { slots: SaveSlotMeta[] } {
    try {
      const data = localStorage.getItem(SaveManager.META_KEY);
      if (data) {
        return JSON.parse(data);
      }
    } catch (e) {
      console.error('Failed to load save meta:', e);
    }
    return { slots: [] };
  }
  
  // 保存元数据
  private saveMeta(meta: { slots: SaveSlotMeta[] }): void {
    try {
      localStorage.setItem(SaveManager.META_KEY, JSON.stringify(meta));
    } catch (e) {
      console.error('Failed to save meta:', e);
    }
  }
  
  // 加载存档
  load(slotId: number): GameSaveData | null {
    try {
      const key = `${SaveManager.SAVE_KEY_PREFIX}${slotId}`;
      const data = localStorage.getItem(key);
      
      if (data) {
        const saveData = JSON.parse(data) as GameSaveData;
        this.currentSlot = slotId;
        console.log(`✅ 加载存档 ${slotId} 成功`);
        return saveData;
      }
    } catch (e) {
      console.error(`Failed to load save ${slotId}:`, e);
    }
    
    return null;
  }
  
  // 保存存档
  save(slotId: number, data: GameSaveData, name?: string): boolean {
    try {
      // 更新元数据
      const meta = this.loadMeta();
      const existingIndex = meta.slots.findIndex(s => s.id === slotId);
      
      const slotMeta: SaveSlotMeta = {
        id: slotId,
        name: name || `存档 ${slotId}`,
        preview: this.generatePreview(data),
        updatedAt: Date.now(),
        playTime: data.playTime,
        playerName: data.playerName,
        playerAge: data.playerAge,
        currentChapter: data.progress.currentChapter,
      };
      
      if (existingIndex >= 0) {
        meta.slots[existingIndex] = slotMeta;
      } else {
        meta.slots.push(slotMeta);
        meta.slots.sort((a, b) => a.id - b.id);
      }
      
      // 保存数据
      const key = `${SaveManager.SAVE_KEY_PREFIX}${slotId}`;
      const saveData: GameSaveData = {
        ...data,
        updatedAt: Date.now(),
      };
      
      localStorage.setItem(key, JSON.stringify(saveData));
      this.saveMeta(meta);
      
      this.currentSlot = slotId;
      console.log(`💾 保存存档 ${slotId} 成功`);
      return true;
      
    } catch (e) {
      console.error(`Failed to save to slot ${slotId}:`, e);
      return false;
    }
  }
  
  // 快速存档
  quickSave(data: GameSaveData): boolean {
    return this.save(0, data, '快速存档');
  }
  
  // 快速加载
  quickLoad(): GameSaveData | null {
    return this.load(0);
  }
  
  // 删除存档
  delete(slotId: number): boolean {
    try {
      const key = `${SaveManager.SAVE_KEY_PREFIX}${slotId}`;
      localStorage.removeItem(key);
      
      // 更新元数据
      const meta = this.loadMeta();
      meta.slots = meta.slots.filter(s => s.id !== slotId);
      this.saveMeta(meta);
      
      console.log(`🗑️ 删除存档 ${slotId} 成功`);
      return true;
    } catch (e) {
      console.error(`Failed to delete save ${slotId}:`, e);
      return false;
    }
  }
  
  // 导出存档为文件
  exportToFile(slotId: number): Blob | null {
    const data = this.load(slotId);
    if (!data) return null;
    
    const exportData = {
      ...data,
      _exportInfo: {
        exportedAt: Date.now(),
        version: data.version,
      }
    };
    
    return new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
  }
  
  // 从文件导入存档
  importFromFile(file: File): Promise<{ success: boolean; slotId?: number; error?: string }> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const data = JSON.parse(content) as GameSaveData;
          
          // 验证数据完整性
          if (!this.validateSaveData(data)) {
            resolve({ success: false, error: '无效的存档文件' });
            return;
          }
          
          // 找到空槽位
          const slotId = this.findEmptySlot();
          if (slotId === -1) {
            resolve({ success: false, error: '存档槽已满' });
            return;
          }
          
          // 保存
          const success = this.save(slotId, data, `导入存档 ${new Date().toLocaleDateString()}`);
          resolve({ success, slotId });
          
        } catch (e) {
          resolve({ success: false, error: '解析文件失败' });
        }
      };
      
      reader.onerror = () => {
        resolve({ success: false, error: '读取文件失败' });
      };
      
      reader.readAsText(file);
    });
  }
  
  // 验证存档数据
  private validateSaveData(data: any): boolean {
    if (!data || typeof data !== 'object') return false;
    
    // 检查必要字段
    const requiredFields = ['version', 'playerName', 'attributes', 'gameTime', 'progress'];
    for (const field of requiredFields) {
      if (!(field in data)) {
        console.warn(`Missing required field: ${field}`);
        return false;
      }
    }
    
    return true;
  }
  
  // 查找空槽位
  private findEmptySlot(): number {
    const meta = this.loadMeta();
    const usedSlots = new Set(meta.slots.map(s => s.id));
    
    for (let i = 1; i <= SaveManager.MAX_SLOTS; i++) {
      if (!usedSlots.has(i)) {
        return i;
      }
    }
    
    return -1;
  }
  
  // 生成存档预览
  private generatePreview(data: GameSaveData): string {
    const date = new Date(data.updatedAt);
    const timeStr = `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
    return `${data.playerName} · ${data.playerAge}岁 · ${timeStr}`;
  }
  
  // 开始自动存档
  startAutosave(getData: () => GameSaveData): void {
    this.stopAutosave();
    
    this.autosaveTimer = window.setInterval(() => {
      const data = getData();
      this.quickSave(data);
      console.log('💾 自动存档完成');
    }, SaveManager.AUTOSAVE_INTERVAL);
    
    console.log('✅ 自动存档已启动');
  }
  
  // 停止自动存档
  stopAutosave(): void {
    if (this.autosaveTimer) {
      clearInterval(this.autosaveTimer);
      this.autosaveTimer = null;
      console.log('⏹️ 自动存档已停止');
    }
  }
  
  // 获取当前存档槽
  getCurrentSlot(): number {
    return this.currentSlot;
  }
  
  // 清除所有存档
  clearAll(): boolean {
    try {
      // 清除所有存档数据
      for (let i = 0; i <= SaveManager.MAX_SLOTS; i++) {
        const key = `${SaveManager.SAVE_KEY_PREFIX}${i}`;
        localStorage.removeItem(key);
      }
      
      // 清除元数据
      localStorage.removeItem(SaveManager.META_KEY);
      
      this.currentSlot = 0;
      console.log('🗑️ 所有存档已清除');
      return true;
    } catch (e) {
      console.error('Failed to clear saves:', e);
      return false;
    }
  }
  
  // 压缩存档（用于减少存储空间）
  compress(data: GameSaveData): string {
    // 移除冗余数据
    const compressed = {
      ...data,
      // 可以在这里添加压缩逻辑
    };
    return JSON.stringify(compressed);
  }
  
  // 获取存储空间使用情况
  getStorageInfo(): { used: number; total: number; percentage: number } {
    let used = 0;
    
    for (let i = 0; i <= SaveManager.MAX_SLOTS; i++) {
      const key = `${SaveManager.SAVE_KEY_PREFIX}${i}`;
      const data = localStorage.getItem(key);
      if (data) {
        used += data.length * 2; // UTF-16 每个字符2字节
      }
    }
    
    // localStorage 通常限制为 5MB
    const total = 5 * 1024 * 1024;
    
    return {
      used,
      total,
      percentage: (used / total) * 100,
    };
  }
}

// 单例实例
let saveManagerInstance: SaveManager | null = null;

export function getSaveManager(): SaveManager {
  if (!saveManagerInstance) {
    saveManagerInstance = new SaveManager();
  }
  return saveManagerInstance;
}